import DOMPurify from "dompurify";

/**
 * Sanitizes note/message HTML before it is passed to `dangerouslySetInnerHTML`.
 *
 * `message_text` is stored without escaping (see `NoteController::putAction`)
 * and only gains markup server-side via `addAnchorTags()` / `parseEmoji()` /
 * `nl2br()` (see `NoteController::indexAction`), none of which strip
 * pre-existing HTML. Anything a user typed — including `<script>` or
 * event-handler attributes like `onerror` — survives the round trip and
 * would otherwise execute in the viewer's browser.
 *
 * The allow-list below only permits what that server-side pipeline can
 * legitimately produce: mention/link anchors, line breaks from `nl2br()`,
 * emoji images from `parseEmoji()`, and basic inline formatting. Iframes
 * (used server-side for YouTube/Vimeo/Vine/Videofarm embeds) are
 * deliberately excluded — allowing an iframe tag back in would let stored,
 * unsanitized input embed an arbitrary attacker-controlled URL.
 */
const ALLOWED_TAGS = ["a", "br", "b", "i", "strong", "em", "span", "div", "img"];
const ALLOWED_ATTR = ["href", "target", "rel", "src", "alt", "title", "class", "data-id", "data-popup"];

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export function sanitizeNoteHtml(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}
